/**
 * Burbuja de mensaje estilo WhatsApp Web.
 *
 * - Mensaje propio: derecha, verde brand sólido, texto blanco, sin avatar.
 * - Mensaje ajeno: izquierda, blanco con borde, avatar visible, nombre del
 *   sender arriba (con color del avatar).
 * - Agrupación: si `groupWithPrev`, no repite avatar/nombre (deja un gap mínimo).
 * - Estado `sending` / `failed` con badges visuales y retry.
 * - Adjuntos: imagen inline con click→fullsize, audio/video con player nativo,
 *   archivos genéricos con icono + nombre + size + descargar.
 */
import { useEffect, useState } from "react";
import { AlertCircle, Download, FileText, Loader2, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Message } from "@/api/messages";
import type { User } from "@/api/users";
import { attachmentsApi } from "@/api/attachments";
import { Avatar } from "./Avatar";

type Props = {
  message: Message;
  sender: User | null;
  isOwn: boolean;
  groupWithPrev: boolean;
  groupWithNext: boolean;
  onRetry?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onReply?: () => void;
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function senderColor(seed: string): string {
  // Mismo algoritmo que Avatar.tsx — color determinístico por user_id.
  const palette = [
    "#1F8A55", "#0F766E", "#2563EB", "#7C3AED", "#DB2777",
    "#DC2626", "#EA580C", "#CA8A04", "#475569", "#0891B2",
  ];
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

export function MessageBubble({
  message,
  sender,
  isOwn,
  groupWithPrev,
  groupWithNext,
  onRetry,
  onEdit,
  onDelete,
  onReply,
}: Props) {
  const status = message._status ?? "sent";
  const showAvatar = !isOwn && !groupWithNext;
  const showName = !isOwn && !groupWithPrev;
  const senderName = sender?.full_name ?? "Usuario";
  const senderId = sender?.id ?? message.sender_user_id;
  const nameColor = senderColor(senderId);
  const isDeleted = message.deleted_at != null;

  // Espaciado: cuando se agrupa con el anterior, menos margen arriba.
  const marginTop = groupWithPrev ? "mt-0.5" : "mt-3";

  // Bordes redondeados que cierran "hacia la cola" del grupo.
  const cornerOwn = !groupWithPrev && !groupWithNext
    ? "rounded-2xl rounded-tr-md"
    : !groupWithPrev
      ? "rounded-2xl rounded-br-md"
      : !groupWithNext
        ? "rounded-2xl rounded-tr-md"
        : "rounded-2xl rounded-r-md";
  const cornerOther = !groupWithPrev && !groupWithNext
    ? "rounded-2xl rounded-tl-md"
    : !groupWithPrev
      ? "rounded-2xl rounded-bl-md"
      : !groupWithNext
        ? "rounded-2xl rounded-tl-md"
        : "rounded-2xl rounded-l-md";

  return (
    <div
      data-testid={`bubble-${message.id}`}
      data-message-id={message.id}
      className={cn("group flex w-full gap-2", marginTop, isOwn ? "justify-end" : "justify-start")}
    >
      {!isOwn && (
        <div className="w-9 shrink-0">
          {showAvatar && <Avatar name={senderName} seed={senderId} size="sm" url={sender?.avatar_url ?? undefined} />}
        </div>
      )}

      <div className={cn("max-w-[78%] min-w-[60px]", isOwn ? "items-end" : "items-start", "flex flex-col")}>
        {showName && (
          <span
            className="mb-0.5 ml-3 text-xs font-semibold"
            style={{ color: nameColor }}
          >
            {senderName}
          </span>
        )}

        <div
          className={cn(
            "msg-in relative px-3 py-2 shadow-sm",
            isOwn
              ? cn("bg-brand-600 text-white", cornerOwn)
              : cn("bg-white text-slate-900 border border-slate-200", cornerOther),
            status === "sending" && "opacity-70",
            status === "failed" && "ring-2 ring-red-400",
          )}
        >
          {isDeleted ? (
            <span className="text-sm italic opacity-70">mensaje eliminado</span>
          ) : (
            <BubbleBody message={message} isOwn={isOwn} />
          )}
          <div
            className={cn(
              "mt-1 flex items-center justify-end gap-1 text-[10px]",
              isOwn ? "text-white/70" : "text-slate-500",
            )}
          >
            <span>{formatTime(message.created_at)}</span>
            {status === "sending" && <Loader2 className="size-3 animate-spin" />}
            {status === "failed" && (
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:underline"
                onClick={onRetry}
              >
                <AlertCircle className="size-3" />
                Reintentar
              </button>
            )}
          </div>

          {/* Menú contextual: solo en mensajes propios no eliminados */}
          {isOwn && !isDeleted && status === "sent" && (onEdit || onDelete || onReply) && (
            <MessageMenu onEdit={onEdit} onDelete={onDelete} onReply={onReply} isOwn={isOwn} />
          )}
          {!isOwn && !isDeleted && onReply && (
            <MessageMenu onReply={onReply} isOwn={isOwn} />
          )}
        </div>
      </div>
    </div>
  );
}

function MessageMenu({
  onEdit,
  onDelete,
  onReply,
  isOwn,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
  onReply?: () => void;
  isOwn: boolean;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div
      className={cn(
        "absolute -top-2 opacity-0 group-hover:opacity-100 transition-opacity",
        isOwn ? "left-0 -translate-x-full pr-1" : "right-0 translate-x-full pl-1",
      )}
    >
      <button
        type="button"
        data-testid="msg-menu-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="rounded-full bg-white p-1 shadow ring-1 ring-slate-200 hover:bg-slate-100"
        title="Más acciones"
      >
        <MoreVertical className="size-3.5 text-slate-600" />
      </button>
      {open && (
        <div
          className={cn(
            "absolute z-10 mt-1 w-32 rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-200 text-left",
            isOwn ? "right-0" : "left-0",
          )}
        >
          {onReply && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-100"
              onClick={onReply}
            >
              Responder
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-100"
              onClick={onEdit}
            >
              <Pencil className="mr-1 inline size-3" /> Editar
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 inline size-3" /> Eliminar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function BubbleBody({ message, isOwn }: { message: Message; isOwn: boolean }) {
  if (message.type === "text") {
    return <div className="whitespace-pre-wrap break-words text-sm">{message.content_text}</div>;
  }
  return (
    <div className="flex flex-col gap-1">
      {message.attachment_id && (
        <AttachmentBlock attachmentId={message.attachment_id} type={message.type} isOwn={isOwn} />
      )}
      {message.content_text && (
        <div className="whitespace-pre-wrap break-words text-sm">{message.content_text}</div>
      )}
    </div>
  );
}

function AttachmentBlock({
  attachmentId,
  type,
  isOwn,
}: {
  attachmentId: string;
  type: Message["type"];
  isOwn: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ mime: string; original_filename: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    attachmentsApi
      .downloadUrl(attachmentId)
      .then((d) => {
        if (cancelled) return;
        setUrl(d.download_url);
        setMeta({ mime: d.mime, original_filename: d.original_filename });
      })
      .catch((e: Error) => !cancelled && setErr(e.message));
    return () => {
      cancelled = true;
    };
  }, [attachmentId]);

  if (err) return <div className="text-xs text-red-600">No se pudo cargar</div>;
  if (!url || !meta)
    return (
      <div className={cn("text-xs", isOwn ? "text-white/70" : "text-slate-500")}>Cargando archivo…</div>
    );

  if (type === "image") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img
          src={url}
          alt={meta.original_filename}
          className="max-h-72 max-w-full rounded-lg object-contain"
        />
      </a>
    );
  }
  if (type === "video") {
    return (
      <video controls className="max-h-72 max-w-full rounded-lg">
        <source src={url} type={meta.mime} />
        <track kind="captions" />
      </video>
    );
  }
  if (type === "audio") {
    return (
      <audio controls className="max-w-full">
        <source src={url} type={meta.mime} />
        <track kind="captions" />
      </audio>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      download={meta.original_filename}
      className={cn(
        "flex items-center gap-2 rounded-lg p-2 text-sm",
        isOwn ? "bg-white/15 hover:bg-white/25" : "border border-slate-200 bg-white hover:bg-slate-50",
      )}
    >
      <FileText className={cn("size-5 shrink-0", isOwn ? "text-white" : "text-brand-600")} />
      <span className="flex-1 truncate">{meta.original_filename}</span>
      <Download className={cn("size-4 shrink-0", isOwn ? "text-white" : "text-slate-500")} />
    </a>
  );
}
